import { createFileRoute } from "@tanstack/react-router";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import { TaskOptions } from "lib/components/builders/communities/tasks/TaskOptions";
import { getLocationBreadcrumbs } from "lib/components/builders/communities/utils/locationUtils";
import { useEstimationTask } from "lib/components/builders/communities/utils/useTasksAPI";
import { FetchErrorMessage } from "lib/components/errors/FetchErrorMessage";
import { getBuilderLocationParentsURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { locationTasksPath, useAppRouterParams, taskPath, taskOptionsPath } from "lib/routeHelpers";
import { useMemo } from "react";
import Loading from "ui/components/Loading";
import withAuthentication from "web/components/withAuthentication";

function TaskOptionsPageReady() {
  const { builderId, taskId } = useAppRouterParams();

  if (!builderId || !taskId) {
    return null;
  }

  return <TaskOptionsPage builderId={builderId} taskId={taskId} />;
}

function TaskOptionsPage({ builderId, taskId }: { builderId: string; taskId: string }) {
  const {
    data: estimationTask,
    loading: loadingEstimationTask,
    error: errorEstimationTask,
  } = useEstimationTask({ builderId, taskId });

  const { data: parents } = useFetch<ClientLocationWithLevel[]>(
    estimationTask ? getBuilderLocationParentsURL({ builderId, locationId: estimationTask.locationId }) : "",
    [builderId, estimationTask],
    {
      handleErrorLocally: true,
    },
  );

  const breadcrumbs = useMemo(() => {
    if (!estimationTask) {
      return [];
    }

    return [
      ...getLocationBreadcrumbs(parents, estimationTask.locationId, builderId),
      {
        text: "Jobs",
        href: locationTasksPath(builderId, estimationTask.locationId),
      },
      {
        text: estimationTask?.name ?? "",
        href: taskPath(builderId, taskId),
      },
      {
        text: "Options",
        href: taskOptionsPath(builderId, taskId),
      },
    ];
  }, [builderId, estimationTask?.name, estimationTask?.locationId, parents, taskId]);

  return (
    <BuilderPortalLayout
      selected="locations"
      pageTitle={estimationTask?.name ?? "Loading Estimation Job Options..."}
      header={`Configure Options for ${estimationTask?.name}`}
      loadingContent={loadingEstimationTask}
      breadcrumbs={breadcrumbs}
      wide
    >
      {loadingEstimationTask && <Loading />}
      {errorEstimationTask && <FetchErrorMessage fetchError={errorEstimationTask} />}
      {estimationTask && <TaskOptions estimationTask={estimationTask} />}
    </BuilderPortalLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/tasks/$task_id/options")({
  component: withAuthentication(TaskOptionsPageReady, {
    permissions: ["VIEW_OPTION_CATALOG"],
    features: ["estimationTasks"],
  }),
});
